import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { connect } from 'react-redux';
import Chart from 'chart.js';

import { LoadingCard } from 'adc-ui-components';
import { flagEnabled } from '../../helpers/featureFlags';
import CircleI from '../svgs/CircleI';
import { getInternetUsage } from '../../actions/usage';
import { getInternetPlan } from '../../actions/internetPlan';
import {
  getChartSlice,
  getMaxChartIndex,
  buildChartDatasets,
  formatUsageMonth,
  formatDayMonth,
  buildUserMessage,
  buildNedUbbContent,
} from '../../helpers/usage';

const isMobileViewport = () => typeof window !== 'undefined'
  && window.matchMedia
  && window.matchMedia('(max-width: 768px)').matches;

export const DataUsage = ({
  loading,
  error,
  data,
  internetPlan,
  canViewUsage,
  onLoad,
}) => {
  const [chartIndex, setChartIndex] = useState(0);
  const [showTable, setShowTable] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const canvasRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!canViewUsage) return;
    onLoad();
  }, [canViewUsage, onLoad]);

  // Adjust tooltip position so it stays on-screen
  useEffect(() => {
    if (!showTooltip || !tooltipRef.current) return;
    const tip = tooltipRef.current;
    const btn = tip.parentNode.querySelector('button');
    if (!btn) return;
    const btnRect = btn.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();
    const vh = window.innerHeight;

    // Reset to default position (right of icon, top-aligned)
    tip.style.top = '';
    tip.style.left = '';

    const box = tip.getBoundingClientRect();

    // If tooltip bottom overflows viewport, flip above the button
    if (box.bottom > vh) {
      const above = btnRect.top - tipRect.height - 6;
      tip.style.top = `${above >= 0 ? above - btnRect.top : -(tipRect.height + 6)}px`;
    }

    // If tooltip right overflows viewport, shift left
    if (box.right > window.innerWidth) {
      tip.style.left = `${-(tipRect.width + 6)}px`;
    }
  }, [showTooltip]);

  const renderChart = useCallback(() => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    const months = data && data.usageMonths;
    if (!months || !months.length || !canvasRef.current) return;

    const slice = getChartSlice(months, chartIndex);
    if (!slice.length) return;

    const { usageValues, overageValues, yAxisMax } = buildChartDatasets(slice);
    const { unitOfMeasure } = slice[0];
    const abbreviated = isMobileViewport();
    const labels = slice.map(({ startDate }) => formatUsageMonth(startDate, abbreviated));

    const datasets = [
      {
        label: 'Usage',
        data: usageValues,
        backgroundColor: '#0272b6',
        hoverBackgroundColor: 'rgba(2, 114, 182, 0.7)',
        hoverBorderWidth: 0,
        hoverBorderColor: 'lightgrey',
      },
    ];

    if (overageValues) {
      datasets.push({
        label: 'Overage',
        data: overageValues,
        backgroundColor: '#c90318',
        hoverBackgroundColor: 'rgba(201, 3, 24, 0.7)',
        hoverBorderWidth: 0,
        hoverBorderColor: 'lightgrey',
      });
    }

    chartInstanceRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        animation: { duration: 10 },
        tooltips: {
          mode: 'label',
          callbacks: {
            label: tooltipItem => `${tooltipItem.yLabel} ${unitOfMeasure}`,
          },
        },
        scales: {
          xAxes: [{ stacked: true, gridLines: { display: false } }],
          yAxes: [{ stacked: true, ticks: { min: 0, suggestedMax: yAxisMax } }],
        },
        legend: { display: false },
      },
    });
  }, [data, chartIndex]);

  useEffect(() => {
    renderChart();
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [renderChart]);

  if (!canViewUsage) return null;

  if (loading) {
    return <LoadingCard />;
  }

  if (error) {
    return (
      <div id="usage">
        <div className="hgroup">
          <h2>Data Usage Overview</h2>
          <p>Review your monthly data usage.</p>
        </div>
        <div className="card card--danger">
          <div className="card__content">
            <p>We were unable to retrieve your usage data. Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data || !data.displayUsage) return null;

  const {
    usageMonths = [],
    policy,
    courtesyAllowed,
    courtesyRemaining,
    courtesyUsed,
    inPaidOverage,
  } = data;
  const tierOfService = internetPlan && internetPlan.tier && internetPlan.tier.tierOfService;
  const currentMonth = usageMonths[usageMonths.length - 1] || {};
  const maxIndex = getMaxChartIndex(usageMonths);
  const canGoPrev = chartIndex < maxIndex;
  const canGoNext = chartIndex > 0;

  const userMessage = buildUserMessage({
    currentMonth,
    policy,
    courtesyAllowed,
    courtesyRemaining,
    courtesyUsed,
    inPaidOverage,
    tierOfService,
  }, flagEnabled);
  const monthlyUsageState = userMessage && userMessage.monthlyUsageState;

  const nedUbb = buildNedUbbContent(data.market && data.market.division, flagEnabled);

  const renderMonthlyUsageState = () => {
    if (!monthlyUsageState) {
      return (
        <span>
          <b>
            {currentMonth.homeUsage}
            {currentMonth.unitOfMeasure}
          </b>
          {' '}
          used this month
        </span>
      );
    }

    if (monthlyUsageState.remaining === undefined) {
      return (
        <span>
          <b>
            {monthlyUsageState.homeUsage}
            {monthlyUsageState.unitOfMeasure}
          </b>
          {' '}
          used this month
        </span>
      );
    }

    return (
      <span>
        <b>
          {monthlyUsageState.remaining}
          {monthlyUsageState.unitOfMeasure}
        </b>
        {' '}
        remaining of
        {' '}
        <b>
          {monthlyUsageState.allowableUsage}
          {monthlyUsageState.unitOfMeasure}
        </b>
        {' '}
        monthly plan
        {monthlyUsageState.overageUsed && (
          <>
            <br />
            You have exceeded your monthly data usage plan. We have automatically
            added an extra
            {' '}
            {monthlyUsageState.additionalIncluded}
            {monthlyUsageState.unitOfMeasure}
            {' '}
            to your account.
          </>
        )}
      </span>
    );
  };

  return (
    <div id="usage">
      <div className="hgroup">
        <h2>Data Usage Overview</h2>
        <p>Review your monthly data usage.</p>
      </div>

      <div className="card-group device-usage">

        {/* NED UBB banner — Northeast division customers only */}
        {nedUbb && (
          <div className={`card card--${nedUbb.color} ubb-card`}>
            <div className="card__content">
              <b>{nedUbb.heading}</b>
              :
              {' '}
              <span>{nedUbb.description}</span>
            </div>
          </div>
        )}

        {/* Card 1 — monthly usage state + date range + disclaimer */}
        <div className="card-group__item">
          <div className="card">
            <div className="card__content usage-info">
              <p>
                {renderMonthlyUsageState()}
                {currentMonth.startDate && currentMonth.endDate && (
                  <span>
                    {' '}
                    (
                    {formatDayMonth(currentMonth.startDate)}
                    {' '}
                    &ndash;
                    {formatDayMonth(currentMonth.endDate)}
                    ).
                  </span>
                )}
                <br />
                <strong>
                  <i className="body4 grey-disclaimer">
                    Data used in the last 24 hours may not be displayed
                  </i>
                </strong>
              </p>
            </div>
          </div>
        </div>

        {usageMonths.length > 0 && (
          <div className="card-group__item">
            <div className="card card--secondary">
              <div className="card__content">

                <div className="chart">
                  <canvas
                    ref={canvasRef}
                    id="bar-chart"
                    className="chart__canvas"
                    width="1024"
                    height="315"
                  />

                  <div className="action chart__pagination">
                    <button
                      type="button"
                      className="button paginate paginate--left"
                      onClick={() => setChartIndex(i => i + 1)}
                      disabled={!canGoPrev}
                      aria-label="Previous months"
                    >
                      <span className="visuallyhidden">Previous</span>
                    </button>
                    <button
                      type="button"
                      className="button paginate paginate--right"
                      onClick={() => setChartIndex(i => i - 1)}
                      disabled={!canGoNext}
                      aria-label="Next months"
                    >
                      <span className="visuallyhidden">Next</span>
                    </button>
                  </div>

                  <button
                    type="button"
                    className="button button--text"
                    onClick={() => setShowTable(s => !s)}
                    aria-expanded={showTable}
                    aria-controls="usage-table"
                  >
                    <span>{showTable ? 'Hide' : 'Show'}</span>
                    {' '}
                    Table Data
                  </button>

                  <div id="usage-table" className={`chart-table${showTable ? '' : ' hide'}`}>
                    <h3>
                      Data Usage by Month
                      <span className="visuallyhidden">Data Chart</span>
                    </h3>
                    <table>
                      <thead>
                        <tr>
                          <th scope="col">Month</th>
                          <th scope="col">Total Monthly Usage</th>
                          <th scope="col">Total Monthly Overage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usageMonths.map(month => (
                          <tr key={month.startDate}>
                            <th scope="row">
                              <b>{month.monthName || formatUsageMonth(month.startDate)}</b>
                              <br />
                              <small>
                                {month.startDate}
                                {' '}
                                &ndash;
                                {' '}
                                {month.endDate}
                              </small>
                            </th>
                            <td>
                              {month.homeUsage}
                              {' '}
                              {month.unitOfMeasure}
                            </td>
                            <td>
                              {month.overageUsed || 0}
                              {' '}
                              {month.unitOfMeasure}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {userMessage && userMessage.additionalMessage
                && Object.keys(userMessage.additionalMessage).length > 0 && (
                <div className="usage-courtesy">
                  <span className="d-inline-block align-top">
                    <span className="usage-courtesy__content">
                      <span>{userMessage.additionalMessage.content}</span>
                      {userMessage.additionalMessage.aside && (
                        <>
                          {' '}
                          <a
                            href={userMessage.additionalMessage.aside.href}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {userMessage.additionalMessage.aside.text}
                          </a>
                        </>
                      )}
                    </span>
                  </span>
                  {userMessage.additionalMessage.tooltip && (
                    <span className="tooltip-modal">
                      <button
                        type="button"
                        className="button button--text"
                        aria-expanded={showTooltip}
                        onClick={() => setShowTooltip(s => !s)}
                      >
                        <CircleI />
                        <span className="screen-reader-text">more info</span>
                      </button>
                      {showTooltip && (
                        <div
                          className="tooltip-modal__mask"
                          role="button"
                          tabIndex={0}
                          aria-label="Close tooltip"
                          onClick={() => setShowTooltip(false)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setShowTooltip(false);
                            }
                          }}
                        />
                      )}
                      <div
                        ref={tooltipRef}
                        className={`tooltip${showTooltip ? ' is-open' : ''}`}
                        role="dialog"
                        aria-hidden={!showTooltip}
                      >
                        <div className="tooltip-content">
                          {userMessage.additionalMessage.tooltip}
                        </div>
                      </div>
                    </span>
                  )}
                </div>
                )}

              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = ({ usage, internetPlan = {}, auth: { accountPerms = [] } = {} }) => ({
  loading: usage.loading,
  error: usage.error,
  data: usage.data,
  internetPlan: internetPlan.data,
  canViewUsage: accountPerms.some(role => role === 'PRIMARY' || role === 'BILLPAY'),
});

const mapDispatchToProps = dispatch => ({
  onLoad: () => {
    dispatch(getInternetUsage());
    dispatch(getInternetPlan());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(DataUsage);
